/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class OfferLettersService {

    /**
     * Create + render an offer preview
     * @param applicantExternalId
     * @param requestBody
     * @returns any the applicant already has an unsent offer for this template, so that offer is re-rendered under the submitted values and returned — no second offer is created. A body that omits `field_values` leaves the saved values alone
     * @throws ApiError
     */
    public static postInternalApiApplicantsOfferLetters(
        applicantExternalId: string,
        requestBody: {
            /**
             * external_id of the selected template
             */
            offer_letter_template_id?: string;
            field_values?: Record<string, string>;
            /**
             * data key -> value the recruiter entered for a key driving a conditional section. Absent keys resolve from document-merge data (applicant, opening, or offer) (AXHE-4001)
             */
            condition_values?: Record<string, string>;
            /**
             * which controls the recruiter moved off the value the form displayed, by key space (AXHE-4673). Write-back to the applicant needs this AND its own recomputation of the prefill to agree; omitting it writes nothing back. Where several fields map to one data key, the fields named here decide that key's sections
             */
            edited_keys?: {
                /**
                 * template field keys
                 */
                field_values?: Array<string>;
                /**
                 * condition data keys
                 */
                condition_values?: Array<string>;
            };
        },
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        status?: 'draft' | 'pending_approval' | 'approved' | 'ready_to_send' | 'send_failed' | 'sent' | 'completed' | 'canceled';
        /**
         * generated from a template, or an uploaded PDF override (AXHE-3720)
         */
        source?: 'generated' | 'uploaded';
        contract_type?: string | null;
        field_values?: Record<string, string>;
        /**
         * data key -> value the recruiter entered for a key driving a conditional section. Absent keys resolve from document-merge data (applicant, opening, or offer) (AXHE-4001)
         */
        condition_values?: Record<string, string>;
        /**
         * which controls this offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so a form editing this offer resolves them under this report
         */
        edited_keys?: {
            /**
             * template field keys
             */
            field_values?: Array<string>;
            /**
             * condition data keys
             */
            condition_values?: Array<string>;
        };
        /**
         * presigned URL to the rendered preview PDF
         */
        rendered_pdf_url?: string | null;
        /**
         * signature/date/initials field placements for an uploaded offer PDF (AXHE-4085). Send [] to clear. Omit the key to leave the stored placements unchanged.
         */
        signature_field_placements?: Array<{
            /**
             * field kind; `date` is auto-filled by Dropbox Sign at signing time
             */
            type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
            /**
             * 0-based index into the signers array (candidate first). Must be < the number of signers this offer will be sent with; an uploaded override has a candidate-only roster, so only 0 is valid there today.
             */
            signer: number;
            /**
             * 1-based page number; must not exceed the offer's rendered PDF page count, unless the PDF cannot be read (skips the upper bound)
             */
            page: number;
            /**
             * left edge, 72 DPI points from the left of the page
             */
            'x': number;
            /**
             * top edge, 72 DPI points down from the top of the page
             */
            'y': number;
            /**
             * width in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            width: number;
            /**
             * height in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            height: number;
            /**
             * whether the signer must complete this field
             */
            required?: boolean;
        }>;
        approval?: {
            required?: boolean;
            /**
             * the approval request now deciding this offer, or null when none is open — approval was not required, it was never submitted, or the last request has been decided. A resubmission raises a NEW request, so this is how a caller following one across an edit finds it (AXHE-4759).
             */
            request_id?: string | null;
            /**
             * the newest approval request on this offer whether or not it is still open, or null when none was ever raised. Ask this, not `required`, whether an approval exists: `required` is recomputed from the rules as they stand now, so a rule narrowed after the offer cleared it reads false on an offer carrying a granted sign-off (AXHE-4952).
             */
            latest_request_id?: string | null;
            /**
             * how many people the open request has emailed on the levels it is asking now, each counted once. An approver who could not be emailed is not counted, nor is a later level or a level a partial re-ask kept. 0 when no request is open.
             */
            notified_approver_count?: number;
            /**
             * approver groups, one per triggering rule
             */
            groups?: Array<{
                rule?: {
                    label?: string;
                    description?: string;
                    field_key?: string | null;
                };
                approvers?: Array<{
                    name?: string;
                    email?: string;
                }>;
            }>;
            /**
             * how the newest request was measured against the approval this offer was edited from, and which levels it therefore did not ask again. Backward-looking, unlike `required` and `groups`: it describes the request that exists, not the one a send from here would raise.
             */
            reask?: {
                /**
                 * `full` asked every applicable level, as a first submission does. `partial` asked some and kept the rest. `carried` asked none and kept them all, so the request was born approved. `exempt` asked none because the stage exempts edits from re-approval. NULL when nothing was measured — every submission on an account without partial re-approval, and every first send on one with it.
                 */
                scope?: 'full' | 'partial' | 'carried' | 'exempt' | null;
                /**
                 * the levels the newest request kept rather than asked, named rather than identified: a client that needs the ladder itself reads it from internal_api/approvals/requests/:external_id, which renders these in position order beside the asked ones.
                 */
                carried_levels?: Array<{
                    name?: string | null;
                    /**
                     * 1-based within its rule
                     */
                    position?: number | null;
                    rule_name?: string | null;
                }>;
            };
            /**
             * what a submit from here would do, read through the approval engine with the policy the submit uses, so an edit is measured the way the submit will measure it. Forward-looking, unlike `reask`. Null when a submit from here decides nothing: a terminal or held offer, one whose approval already released it, or one waiting on an open request.
             */
            plan?: {
                /**
                 * `approval`: the offer waits on the levels `rules` shows `pending`. `send`: it goes to the applicant. `hold`: nobody is asked, and the stage holds the approved offer for a recruiter to send.
                 */
                outcome?: 'approval' | 'send' | 'hold';
                /**
                 * the ladder the request a submit raises would show, in the shape of `rule_groups` on GET internal_api/approvals/requests/{external_id}. A level the submit asks reads `pending`, or `skipped` when its condition does not match; the submit opens each rule's first one. A level an edit keeps reads `approved` and `carried`. Approver emails and comments are null. Empty when no request would be raised.
                 */
                rules?: Array<any>;
                /**
                 * send back as `approval_plan_digest` with POST .../submit_for_approval, which refuses with 409 rather than route the offer differently from this plan. It covers the route, not the approvers: the submit resolves them afresh, so its request can ask people other than `rules` names. Absent from POST .../approval_plan, which answers for an edit not yet saved.
                 */
                digest?: string;
            } | null;
        };
        cta?: 'send_for_approval' | 'send_to_candidate';
        /**
         * whether this account may replace the generated doc with an uploaded PDF (AXHE-3720)
         */
        upload_override_enabled?: boolean;
        /**
         * whether POST .../revert would be accepted for this offer (AXHE-4266): it is an upload override, is not terminal, and still has a template to render. Drives the "Revert to template" control — do not infer it from `source` alone
         */
        can_revert_to_template?: boolean;
        /**
         * whether POST .../revise would be accepted for this offer (AXHE-4425): it is sent, is not an upload override, and no signer has signed it yet. Drives the "Edit" control — do not infer it from `status` alone, which cannot tell a signed offer from an unsigned one
         */
        can_revise?: boolean;
        /**
         * whether POST .../cancel would be accepted for this offer (AXHE-4682): it is sent, pending approval, approved, ready to send, or send-failed; is not an unsent revision; and no signer has signed it. Drives the "Cancel offer" control — do not infer it from `status` alone
         */
        can_cancel?: boolean;
        /**
         * whether POST .../discard_edit would be accepted for this offer: it is the draft an edit made of an offer its stage held at approval, nothing has replaced its document since, and the approval it was held under is still its newest request. Drives "Discard changes" on that edit — do not infer it from `status` alone
         */
        can_discard_edit?: boolean;
        /**
         * whether this offer replaces an earlier one. Its CTA reads "Re-send to applicant" rather than "Send"
         */
        revision?: boolean;
        /**
         * whether the stage's landing job sent this offer with no recruiter
         */
        auto_sent?: boolean;
        /**
         * past versions of this offer, newest first — the version history panel (AXHE-4425). Excludes the offer being viewed, which is the one currently in the applicant's portal. Empty for a first-time offer. Download a version through GET /internal_api/offer_letters/{its id}/download
         */
        versions?: Array<{
            /**
             * external_id of that version
             */
            id?: string;
            /**
             * when that version went to the candidate (ISO 8601)
             */
            sent_at?: string | null;
            /**
             * display name of the user who sent it; null if that user has since been removed, or if the stage's landing job sent it (see `auto_sent`)
             */
            sent_by?: string | null;
            /**
             * whether the stage's landing job sent that version with no recruiter
             */
            auto_sent?: boolean;
            /**
             * whether the applicant has stopped holding this version (AXHE-4950): true unless it is still `sent`. Do not infer it from the row's position — a supersede whose vendor void failed leaves the predecessor live, and it is still listed here
             */
            retired?: boolean;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/applicants/{applicant_external_id}/offer_letters',
            path: {
                'applicant_external_id': applicantExternalId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                409: `the applicant holds a signed offer for this template, or the newest offer for it is pending approval, approved, ready to send, or sent and still cancelable; nothing was created, and that offer comes back`,
            },
        });
    }

    /**
     * Fetch a rendered offer preview
     * @param externalId
     * @returns any returns the preview payload
     * @throws ApiError
     */
    public static getInternalApiOfferLetters(
        externalId: string,
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        status?: 'draft' | 'pending_approval' | 'approved' | 'ready_to_send' | 'send_failed' | 'sent' | 'completed' | 'canceled';
        /**
         * generated from a template, or an uploaded PDF override (AXHE-3720)
         */
        source?: 'generated' | 'uploaded';
        contract_type?: string | null;
        field_values?: Record<string, string>;
        /**
         * data key -> value the recruiter entered for a key driving a conditional section. Absent keys resolve from document-merge data (applicant, opening, or offer) (AXHE-4001)
         */
        condition_values?: Record<string, string>;
        /**
         * which controls this offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so a form editing this offer resolves them under this report
         */
        edited_keys?: {
            /**
             * template field keys
             */
            field_values?: Array<string>;
            /**
             * condition data keys
             */
            condition_values?: Array<string>;
        };
        /**
         * presigned URL to the rendered preview PDF
         */
        rendered_pdf_url?: string | null;
        /**
         * signature/date/initials field placements for an uploaded offer PDF (AXHE-4085). Send [] to clear. Omit the key to leave the stored placements unchanged.
         */
        signature_field_placements?: Array<{
            /**
             * field kind; `date` is auto-filled by Dropbox Sign at signing time
             */
            type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
            /**
             * 0-based index into the signers array (candidate first). Must be < the number of signers this offer will be sent with; an uploaded override has a candidate-only roster, so only 0 is valid there today.
             */
            signer: number;
            /**
             * 1-based page number; must not exceed the offer's rendered PDF page count, unless the PDF cannot be read (skips the upper bound)
             */
            page: number;
            /**
             * left edge, 72 DPI points from the left of the page
             */
            'x': number;
            /**
             * top edge, 72 DPI points down from the top of the page
             */
            'y': number;
            /**
             * width in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            width: number;
            /**
             * height in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            height: number;
            /**
             * whether the signer must complete this field
             */
            required?: boolean;
        }>;
        approval?: {
            required?: boolean;
            /**
             * the approval request now deciding this offer, or null when none is open — approval was not required, it was never submitted, or the last request has been decided. A resubmission raises a NEW request, so this is how a caller following one across an edit finds it (AXHE-4759).
             */
            request_id?: string | null;
            /**
             * the newest approval request on this offer whether or not it is still open, or null when none was ever raised. Ask this, not `required`, whether an approval exists: `required` is recomputed from the rules as they stand now, so a rule narrowed after the offer cleared it reads false on an offer carrying a granted sign-off (AXHE-4952).
             */
            latest_request_id?: string | null;
            /**
             * how many people the open request has emailed on the levels it is asking now, each counted once. An approver who could not be emailed is not counted, nor is a later level or a level a partial re-ask kept. 0 when no request is open.
             */
            notified_approver_count?: number;
            /**
             * approver groups, one per triggering rule
             */
            groups?: Array<{
                rule?: {
                    label?: string;
                    description?: string;
                    field_key?: string | null;
                };
                approvers?: Array<{
                    name?: string;
                    email?: string;
                }>;
            }>;
            /**
             * how the newest request was measured against the approval this offer was edited from, and which levels it therefore did not ask again. Backward-looking, unlike `required` and `groups`: it describes the request that exists, not the one a send from here would raise.
             */
            reask?: {
                /**
                 * `full` asked every applicable level, as a first submission does. `partial` asked some and kept the rest. `carried` asked none and kept them all, so the request was born approved. `exempt` asked none because the stage exempts edits from re-approval. NULL when nothing was measured — every submission on an account without partial re-approval, and every first send on one with it.
                 */
                scope?: 'full' | 'partial' | 'carried' | 'exempt' | null;
                /**
                 * the levels the newest request kept rather than asked, named rather than identified: a client that needs the ladder itself reads it from internal_api/approvals/requests/:external_id, which renders these in position order beside the asked ones.
                 */
                carried_levels?: Array<{
                    name?: string | null;
                    /**
                     * 1-based within its rule
                     */
                    position?: number | null;
                    rule_name?: string | null;
                }>;
            };
            /**
             * what a submit from here would do, read through the approval engine with the policy the submit uses, so an edit is measured the way the submit will measure it. Forward-looking, unlike `reask`. Null when a submit from here decides nothing: a terminal or held offer, one whose approval already released it, or one waiting on an open request.
             */
            plan?: {
                /**
                 * `approval`: the offer waits on the levels `rules` shows `pending`. `send`: it goes to the applicant. `hold`: nobody is asked, and the stage holds the approved offer for a recruiter to send.
                 */
                outcome?: 'approval' | 'send' | 'hold';
                /**
                 * the ladder the request a submit raises would show, in the shape of `rule_groups` on GET internal_api/approvals/requests/{external_id}. A level the submit asks reads `pending`, or `skipped` when its condition does not match; the submit opens each rule's first one. A level an edit keeps reads `approved` and `carried`. Approver emails and comments are null. Empty when no request would be raised.
                 */
                rules?: Array<any>;
                /**
                 * send back as `approval_plan_digest` with POST .../submit_for_approval, which refuses with 409 rather than route the offer differently from this plan. It covers the route, not the approvers: the submit resolves them afresh, so its request can ask people other than `rules` names. Absent from POST .../approval_plan, which answers for an edit not yet saved.
                 */
                digest?: string;
            } | null;
        };
        cta?: 'send_for_approval' | 'send_to_candidate';
        /**
         * whether this account may replace the generated doc with an uploaded PDF (AXHE-3720)
         */
        upload_override_enabled?: boolean;
        /**
         * whether POST .../revert would be accepted for this offer (AXHE-4266): it is an upload override, is not terminal, and still has a template to render. Drives the "Revert to template" control — do not infer it from `source` alone
         */
        can_revert_to_template?: boolean;
        /**
         * whether POST .../revise would be accepted for this offer (AXHE-4425): it is sent, is not an upload override, and no signer has signed it yet. Drives the "Edit" control — do not infer it from `status` alone, which cannot tell a signed offer from an unsigned one
         */
        can_revise?: boolean;
        /**
         * whether POST .../cancel would be accepted for this offer (AXHE-4682): it is sent, pending approval, approved, ready to send, or send-failed; is not an unsent revision; and no signer has signed it. Drives the "Cancel offer" control — do not infer it from `status` alone
         */
        can_cancel?: boolean;
        /**
         * whether POST .../discard_edit would be accepted for this offer: it is the draft an edit made of an offer its stage held at approval, nothing has replaced its document since, and the approval it was held under is still its newest request. Drives "Discard changes" on that edit — do not infer it from `status` alone
         */
        can_discard_edit?: boolean;
        /**
         * whether this offer replaces an earlier one. Its CTA reads "Re-send to applicant" rather than "Send"
         */
        revision?: boolean;
        /**
         * whether the stage's landing job sent this offer with no recruiter
         */
        auto_sent?: boolean;
        /**
         * past versions of this offer, newest first — the version history panel (AXHE-4425). Excludes the offer being viewed, which is the one currently in the applicant's portal. Empty for a first-time offer. Download a version through GET /internal_api/offer_letters/{its id}/download
         */
        versions?: Array<{
            /**
             * external_id of that version
             */
            id?: string;
            /**
             * when that version went to the candidate (ISO 8601)
             */
            sent_at?: string | null;
            /**
             * display name of the user who sent it; null if that user has since been removed, or if the stage's landing job sent it (see `auto_sent`)
             */
            sent_by?: string | null;
            /**
             * whether the stage's landing job sent that version with no recruiter
             */
            auto_sent?: boolean;
            /**
             * whether the applicant has stopped holding this version (AXHE-4950): true unless it is still `sent`. Do not infer it from the row's position — a supersede whose vendor void failed leaves the predecessor live, and it is still listed here
             */
            retired?: boolean;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/offer_letters/{external_id}',
            path: {
                'external_id': externalId,
            },
        });
    }

    /**
     * Edit offer values and/or signature-field placements (re-checks approval)
     * @param externalId
     * @param requestBody
     * @returns any returns the re-rendered preview, with any stored placements
     * @throws ApiError
     */
    public static patchInternalApiOfferLetters(
        externalId: string,
        requestBody: {
            field_values?: Record<string, string>;
            /**
             * data key -> value the recruiter entered for a key driving a conditional section. Absent keys resolve from document-merge data (applicant, opening, or offer) (AXHE-4001)
             */
            condition_values?: Record<string, string>;
            /**
             * which controls the recruiter moved off the value the form displayed, by key space (AXHE-4673). Write-back to the applicant needs this AND its own recomputation of the prefill to agree; omitting it writes nothing back. Where several fields map to one data key, the fields named here decide that key's sections
             */
            edited_keys?: {
                /**
                 * template field keys
                 */
                field_values?: Array<string>;
                /**
                 * condition data keys
                 */
                condition_values?: Array<string>;
            };
            /**
             * signature/date/initials field placements for an uploaded offer PDF (AXHE-4085). Send [] to clear. Omit the key to leave the stored placements unchanged.
             */
            signature_field_placements?: Array<{
                /**
                 * field kind; `date` is auto-filled by Dropbox Sign at signing time
                 */
                type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
                /**
                 * 0-based index into the signers array (candidate first). Must be < the number of signers this offer will be sent with; an uploaded override has a candidate-only roster, so only 0 is valid there today.
                 */
                signer: number;
                /**
                 * 1-based page number; must not exceed the offer's rendered PDF page count, unless the PDF cannot be read (skips the upper bound)
                 */
                page: number;
                /**
                 * left edge, 72 DPI points from the left of the page
                 */
                'x': number;
                /**
                 * top edge, 72 DPI points down from the top of the page
                 */
                'y': number;
                /**
                 * width in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
                 */
                width: number;
                /**
                 * height in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
                 */
                height: number;
                /**
                 * whether the signer must complete this field
                 */
                required?: boolean;
            }>;
        },
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        status?: 'draft' | 'pending_approval' | 'approved' | 'ready_to_send' | 'send_failed' | 'sent' | 'completed' | 'canceled';
        /**
         * generated from a template, or an uploaded PDF override (AXHE-3720)
         */
        source?: 'generated' | 'uploaded';
        contract_type?: string | null;
        field_values?: Record<string, string>;
        /**
         * data key -> value the recruiter entered for a key driving a conditional section. Absent keys resolve from document-merge data (applicant, opening, or offer) (AXHE-4001)
         */
        condition_values?: Record<string, string>;
        /**
         * which controls this offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so a form editing this offer resolves them under this report
         */
        edited_keys?: {
            /**
             * template field keys
             */
            field_values?: Array<string>;
            /**
             * condition data keys
             */
            condition_values?: Array<string>;
        };
        /**
         * presigned URL to the rendered preview PDF
         */
        rendered_pdf_url?: string | null;
        /**
         * signature/date/initials field placements for an uploaded offer PDF (AXHE-4085). Send [] to clear. Omit the key to leave the stored placements unchanged.
         */
        signature_field_placements?: Array<{
            /**
             * field kind; `date` is auto-filled by Dropbox Sign at signing time
             */
            type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
            /**
             * 0-based index into the signers array (candidate first). Must be < the number of signers this offer will be sent with; an uploaded override has a candidate-only roster, so only 0 is valid there today.
             */
            signer: number;
            /**
             * 1-based page number; must not exceed the offer's rendered PDF page count, unless the PDF cannot be read (skips the upper bound)
             */
            page: number;
            /**
             * left edge, 72 DPI points from the left of the page
             */
            'x': number;
            /**
             * top edge, 72 DPI points down from the top of the page
             */
            'y': number;
            /**
             * width in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            width: number;
            /**
             * height in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            height: number;
            /**
             * whether the signer must complete this field
             */
            required?: boolean;
        }>;
        approval?: {
            required?: boolean;
            /**
             * the approval request now deciding this offer, or null when none is open — approval was not required, it was never submitted, or the last request has been decided. A resubmission raises a NEW request, so this is how a caller following one across an edit finds it (AXHE-4759).
             */
            request_id?: string | null;
            /**
             * the newest approval request on this offer whether or not it is still open, or null when none was ever raised. Ask this, not `required`, whether an approval exists: `required` is recomputed from the rules as they stand now, so a rule narrowed after the offer cleared it reads false on an offer carrying a granted sign-off (AXHE-4952).
             */
            latest_request_id?: string | null;
            /**
             * how many people the open request has emailed on the levels it is asking now, each counted once. An approver who could not be emailed is not counted, nor is a later level or a level a partial re-ask kept. 0 when no request is open.
             */
            notified_approver_count?: number;
            /**
             * approver groups, one per triggering rule
             */
            groups?: Array<{
                rule?: {
                    label?: string;
                    description?: string;
                    field_key?: string | null;
                };
                approvers?: Array<{
                    name?: string;
                    email?: string;
                }>;
            }>;
            /**
             * how the newest request was measured against the approval this offer was edited from, and which levels it therefore did not ask again. Backward-looking, unlike `required` and `groups`: it describes the request that exists, not the one a send from here would raise.
             */
            reask?: {
                /**
                 * `full` asked every applicable level, as a first submission does. `partial` asked some and kept the rest. `carried` asked none and kept them all, so the request was born approved. `exempt` asked none because the stage exempts edits from re-approval. NULL when nothing was measured — every submission on an account without partial re-approval, and every first send on one with it.
                 */
                scope?: 'full' | 'partial' | 'carried' | 'exempt' | null;
                /**
                 * the levels the newest request kept rather than asked, named rather than identified: a client that needs the ladder itself reads it from internal_api/approvals/requests/:external_id, which renders these in position order beside the asked ones.
                 */
                carried_levels?: Array<{
                    name?: string | null;
                    /**
                     * 1-based within its rule
                     */
                    position?: number | null;
                    rule_name?: string | null;
                }>;
            };
            /**
             * what a submit from here would do, read through the approval engine with the policy the submit uses, so an edit is measured the way the submit will measure it. Forward-looking, unlike `reask`. Null when a submit from here decides nothing: a terminal or held offer, one whose approval already released it, or one waiting on an open request.
             */
            plan?: {
                /**
                 * `approval`: the offer waits on the levels `rules` shows `pending`. `send`: it goes to the applicant. `hold`: nobody is asked, and the stage holds the approved offer for a recruiter to send.
                 */
                outcome?: 'approval' | 'send' | 'hold';
                /**
                 * the ladder the request a submit raises would show, in the shape of `rule_groups` on GET internal_api/approvals/requests/{external_id}. A level the submit asks reads `pending`, or `skipped` when its condition does not match; the submit opens each rule's first one. A level an edit keeps reads `approved` and `carried`. Approver emails and comments are null. Empty when no request would be raised.
                 */
                rules?: Array<any>;
                /**
                 * send back as `approval_plan_digest` with POST .../submit_for_approval, which refuses with 409 rather than route the offer differently from this plan. It covers the route, not the approvers: the submit resolves them afresh, so its request can ask people other than `rules` names. Absent from POST .../approval_plan, which answers for an edit not yet saved.
                 */
                digest?: string;
            } | null;
        };
        cta?: 'send_for_approval' | 'send_to_candidate';
        /**
         * whether this account may replace the generated doc with an uploaded PDF (AXHE-3720)
         */
        upload_override_enabled?: boolean;
        /**
         * whether POST .../revert would be accepted for this offer (AXHE-4266): it is an upload override, is not terminal, and still has a template to render. Drives the "Revert to template" control — do not infer it from `source` alone
         */
        can_revert_to_template?: boolean;
        /**
         * whether POST .../revise would be accepted for this offer (AXHE-4425): it is sent, is not an upload override, and no signer has signed it yet. Drives the "Edit" control — do not infer it from `status` alone, which cannot tell a signed offer from an unsigned one
         */
        can_revise?: boolean;
        /**
         * whether POST .../cancel would be accepted for this offer (AXHE-4682): it is sent, pending approval, approved, ready to send, or send-failed; is not an unsent revision; and no signer has signed it. Drives the "Cancel offer" control — do not infer it from `status` alone
         */
        can_cancel?: boolean;
        /**
         * whether POST .../discard_edit would be accepted for this offer: it is the draft an edit made of an offer its stage held at approval, nothing has replaced its document since, and the approval it was held under is still its newest request. Drives "Discard changes" on that edit — do not infer it from `status` alone
         */
        can_discard_edit?: boolean;
        /**
         * whether this offer replaces an earlier one. Its CTA reads "Re-send to applicant" rather than "Send"
         */
        revision?: boolean;
        /**
         * whether the stage's landing job sent this offer with no recruiter
         */
        auto_sent?: boolean;
        /**
         * past versions of this offer, newest first — the version history panel (AXHE-4425). Excludes the offer being viewed, which is the one currently in the applicant's portal. Empty for a first-time offer. Download a version through GET /internal_api/offer_letters/{its id}/download
         */
        versions?: Array<{
            /**
             * external_id of that version
             */
            id?: string;
            /**
             * when that version went to the candidate (ISO 8601)
             */
            sent_at?: string | null;
            /**
             * display name of the user who sent it; null if that user has since been removed, or if the stage's landing job sent it (see `auto_sent`)
             */
            sent_by?: string | null;
            /**
             * whether the stage's landing job sent that version with no recruiter
             */
            auto_sent?: boolean;
            /**
             * whether the applicant has stopped holding this version (AXHE-4950): true unless it is still `sent`. Do not infer it from the row's position — a supersede whose vendor void failed leaves the predecessor live, and it is still listed here
             */
            retired?: boolean;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/offer_letters/{external_id}',
            path: {
                'external_id': externalId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `a placement is malformed (unknown type, geometry off the contract, or a signer index the offer has no signer for)`,
            },
        });
    }

    /**
     * Discard an unsent revision — Cancel, or closing the panel (AXHE-4425)
     * Throws away an edit in progress, as if it had never been opened. The offer it supersedes is untouched and stays live for the candidate. Soft delete, so the abandoned draft remains retrievable for support while every read path stops returning it. Accepts ONLY an unsent revision: a first-time draft is an offer someone is still writing, and a sent revision is a delivered document. A revision awaiting approval may be discarded, which withdraws the open approval request with it. NOT idempotent — every read path is scoped past the soft delete, so a repeated DELETE on the same id returns 404, not 204. Treat the 404 as "already discarded" rather than retrying.
     * @param externalId
     * @returns void
     * @throws ApiError
     */
    public static deleteInternalApiOfferLetters(
        externalId: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/offer_letters/{external_id}',
            path: {
                'external_id': externalId,
            },
            errors: {
                422: `the offer is not an unsent revision`,
            },
        });
    }

    /**
     * Submit per the resolved CTA (send-for-approval vs send-to-candidate)
     * @param externalId
     * @param requestBody
     * @returns any returns the offer with its updated status
     * @throws ApiError
     */
    public static postInternalApiOfferLettersSubmitForApproval(
        externalId: string,
        requestBody?: {
            /**
             * `approval.plan.digest` from the preview this submit follows. When present, a submit that decides the offer's route is refused with 409, writing and sending nothing, if it would decide it differently from that preview. A submit that finds the offer already waiting on approval, or already released, decides nothing and answers with the offer as it stands. Omit it to submit whatever the offer routes to now.
             */
            approval_plan_digest?: string;
        },
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        status?: 'draft' | 'pending_approval' | 'approved' | 'ready_to_send' | 'send_failed' | 'sent' | 'completed' | 'canceled';
        /**
         * generated from a template, or an uploaded PDF override (AXHE-3720)
         */
        source?: 'generated' | 'uploaded';
        contract_type?: string | null;
        field_values?: Record<string, string>;
        /**
         * data key -> value the recruiter entered for a key driving a conditional section. Absent keys resolve from document-merge data (applicant, opening, or offer) (AXHE-4001)
         */
        condition_values?: Record<string, string>;
        /**
         * which controls this offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so a form editing this offer resolves them under this report
         */
        edited_keys?: {
            /**
             * template field keys
             */
            field_values?: Array<string>;
            /**
             * condition data keys
             */
            condition_values?: Array<string>;
        };
        /**
         * presigned URL to the rendered preview PDF
         */
        rendered_pdf_url?: string | null;
        /**
         * signature/date/initials field placements for an uploaded offer PDF (AXHE-4085). Send [] to clear. Omit the key to leave the stored placements unchanged.
         */
        signature_field_placements?: Array<{
            /**
             * field kind; `date` is auto-filled by Dropbox Sign at signing time
             */
            type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
            /**
             * 0-based index into the signers array (candidate first). Must be < the number of signers this offer will be sent with; an uploaded override has a candidate-only roster, so only 0 is valid there today.
             */
            signer: number;
            /**
             * 1-based page number; must not exceed the offer's rendered PDF page count, unless the PDF cannot be read (skips the upper bound)
             */
            page: number;
            /**
             * left edge, 72 DPI points from the left of the page
             */
            'x': number;
            /**
             * top edge, 72 DPI points down from the top of the page
             */
            'y': number;
            /**
             * width in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            width: number;
            /**
             * height in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            height: number;
            /**
             * whether the signer must complete this field
             */
            required?: boolean;
        }>;
        approval?: {
            required?: boolean;
            /**
             * the approval request now deciding this offer, or null when none is open — approval was not required, it was never submitted, or the last request has been decided. A resubmission raises a NEW request, so this is how a caller following one across an edit finds it (AXHE-4759).
             */
            request_id?: string | null;
            /**
             * the newest approval request on this offer whether or not it is still open, or null when none was ever raised. Ask this, not `required`, whether an approval exists: `required` is recomputed from the rules as they stand now, so a rule narrowed after the offer cleared it reads false on an offer carrying a granted sign-off (AXHE-4952).
             */
            latest_request_id?: string | null;
            /**
             * how many people the open request has emailed on the levels it is asking now, each counted once. An approver who could not be emailed is not counted, nor is a later level or a level a partial re-ask kept. 0 when no request is open.
             */
            notified_approver_count?: number;
            /**
             * approver groups, one per triggering rule
             */
            groups?: Array<{
                rule?: {
                    label?: string;
                    description?: string;
                    field_key?: string | null;
                };
                approvers?: Array<{
                    name?: string;
                    email?: string;
                }>;
            }>;
            /**
             * how the newest request was measured against the approval this offer was edited from, and which levels it therefore did not ask again. Backward-looking, unlike `required` and `groups`: it describes the request that exists, not the one a send from here would raise.
             */
            reask?: {
                /**
                 * `full` asked every applicable level, as a first submission does. `partial` asked some and kept the rest. `carried` asked none and kept them all, so the request was born approved. `exempt` asked none because the stage exempts edits from re-approval. NULL when nothing was measured — every submission on an account without partial re-approval, and every first send on one with it.
                 */
                scope?: 'full' | 'partial' | 'carried' | 'exempt' | null;
                /**
                 * the levels the newest request kept rather than asked, named rather than identified: a client that needs the ladder itself reads it from internal_api/approvals/requests/:external_id, which renders these in position order beside the asked ones.
                 */
                carried_levels?: Array<{
                    name?: string | null;
                    /**
                     * 1-based within its rule
                     */
                    position?: number | null;
                    rule_name?: string | null;
                }>;
            };
            /**
             * what a submit from here would do, read through the approval engine with the policy the submit uses, so an edit is measured the way the submit will measure it. Forward-looking, unlike `reask`. Null when a submit from here decides nothing: a terminal or held offer, one whose approval already released it, or one waiting on an open request.
             */
            plan?: {
                /**
                 * `approval`: the offer waits on the levels `rules` shows `pending`. `send`: it goes to the applicant. `hold`: nobody is asked, and the stage holds the approved offer for a recruiter to send.
                 */
                outcome?: 'approval' | 'send' | 'hold';
                /**
                 * the ladder the request a submit raises would show, in the shape of `rule_groups` on GET internal_api/approvals/requests/{external_id}. A level the submit asks reads `pending`, or `skipped` when its condition does not match; the submit opens each rule's first one. A level an edit keeps reads `approved` and `carried`. Approver emails and comments are null. Empty when no request would be raised.
                 */
                rules?: Array<any>;
                /**
                 * send back as `approval_plan_digest` with POST .../submit_for_approval, which refuses with 409 rather than route the offer differently from this plan. It covers the route, not the approvers: the submit resolves them afresh, so its request can ask people other than `rules` names. Absent from POST .../approval_plan, which answers for an edit not yet saved.
                 */
                digest?: string;
            } | null;
        };
        cta?: 'send_for_approval' | 'send_to_candidate';
        /**
         * whether this account may replace the generated doc with an uploaded PDF (AXHE-3720)
         */
        upload_override_enabled?: boolean;
        /**
         * whether POST .../revert would be accepted for this offer (AXHE-4266): it is an upload override, is not terminal, and still has a template to render. Drives the "Revert to template" control — do not infer it from `source` alone
         */
        can_revert_to_template?: boolean;
        /**
         * whether POST .../revise would be accepted for this offer (AXHE-4425): it is sent, is not an upload override, and no signer has signed it yet. Drives the "Edit" control — do not infer it from `status` alone, which cannot tell a signed offer from an unsigned one
         */
        can_revise?: boolean;
        /**
         * whether POST .../cancel would be accepted for this offer (AXHE-4682): it is sent, pending approval, approved, ready to send, or send-failed; is not an unsent revision; and no signer has signed it. Drives the "Cancel offer" control — do not infer it from `status` alone
         */
        can_cancel?: boolean;
        /**
         * whether POST .../discard_edit would be accepted for this offer: it is the draft an edit made of an offer its stage held at approval, nothing has replaced its document since, and the approval it was held under is still its newest request. Drives "Discard changes" on that edit — do not infer it from `status` alone
         */
        can_discard_edit?: boolean;
        /**
         * whether this offer replaces an earlier one. Its CTA reads "Re-send to applicant" rather than "Send"
         */
        revision?: boolean;
        /**
         * whether the stage's landing job sent this offer with no recruiter
         */
        auto_sent?: boolean;
        /**
         * past versions of this offer, newest first — the version history panel (AXHE-4425). Excludes the offer being viewed, which is the one currently in the applicant's portal. Empty for a first-time offer. Download a version through GET /internal_api/offer_letters/{its id}/download
         */
        versions?: Array<{
            /**
             * external_id of that version
             */
            id?: string;
            /**
             * when that version went to the candidate (ISO 8601)
             */
            sent_at?: string | null;
            /**
             * display name of the user who sent it; null if that user has since been removed, or if the stage's landing job sent it (see `auto_sent`)
             */
            sent_by?: string | null;
            /**
             * whether the stage's landing job sent that version with no recruiter
             */
            auto_sent?: boolean;
            /**
             * whether the applicant has stopped holding this version (AXHE-4950): true unless it is still `sent`. Do not infer it from the row's position — a supersede whose vendor void failed leaves the predecessor live, and it is still listed here
             */
            retired?: boolean;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/offer_letters/{external_id}/submit_for_approval',
            path: {
                'external_id': externalId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                409: `the submit would do something other than the preview its digest names; nothing was written or sent`,
            },
        });
    }

    /**
     * What a submit would do once an edit is saved, without saving it
     * Applies the edit in the body to the offer in memory, as PATCH .../{external_id} would, and answers the plan a submit would then carry out. Nothing is written: no values, no status, and no cancelled approval request.
     * @param externalId
     * @param requestBody
     * @returns any the plan for the edited offer; nothing is written
     * @throws ApiError
     */
    public static postInternalApiOfferLettersApprovalPlan(
        externalId: string,
        requestBody: {
            field_values?: Record<string, string>;
            /**
             * data key -> value the recruiter entered for a key driving a conditional section
             */
            condition_values?: Record<string, string>;
            /**
             * signature/date/initials field placements for an uploaded offer PDF (AXHE-4085). Send [] to clear. Omit the key to leave the stored placements unchanged.
             */
            signature_field_placements?: Array<{
                /**
                 * field kind; `date` is auto-filled by Dropbox Sign at signing time
                 */
                type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
                /**
                 * 0-based index into the signers array (candidate first). Must be < the number of signers this offer will be sent with; an uploaded override has a candidate-only roster, so only 0 is valid there today.
                 */
                signer: number;
                /**
                 * 1-based page number; must not exceed the offer's rendered PDF page count, unless the PDF cannot be read (skips the upper bound)
                 */
                page: number;
                /**
                 * left edge, 72 DPI points from the left of the page
                 */
                'x': number;
                /**
                 * top edge, 72 DPI points down from the top of the page
                 */
                'y': number;
                /**
                 * width in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
                 */
                width: number;
                /**
                 * height in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
                 */
                height: number;
                /**
                 * whether the signer must complete this field
                 */
                required?: boolean;
            }>;
            /**
             * which controls this session moved off the value the form displayed, as PATCH .../{external_id} takes them. Joined with the offer's stored report, they pick which of several fields mapped to one data key decides that key's sections, so the readiness check matches the save
             */
            edited_keys?: {
                /**
                 * template field keys
                 */
                field_values?: Array<string>;
                /**
                 * condition data keys
                 */
                condition_values?: Array<string>;
            };
        },
    ): CancelablePromise<{
        /**
         * what a submit from here would do, read through the approval engine with the policy the submit uses, so an edit is measured the way the submit will measure it. Forward-looking, unlike `reask`. Null when a submit from here decides nothing: a terminal or held offer, one whose approval already released it, or one waiting on an open request.
         */
        plan?: {
            /**
             * `approval`: the offer waits on the levels `rules` shows `pending`. `send`: it goes to the applicant. `hold`: nobody is asked, and the stage holds the approved offer for a recruiter to send.
             */
            outcome?: 'approval' | 'send' | 'hold';
            /**
             * the ladder the request a submit raises would show, in the shape of `rule_groups` on GET internal_api/approvals/requests/{external_id}. A level the submit asks reads `pending`, or `skipped` when its condition does not match; the submit opens each rule's first one. A level an edit keeps reads `approved` and `carried`. Approver emails and comments are null. Empty when no request would be raised.
             */
            rules?: Array<any>;
            /**
             * send back as `approval_plan_digest` with POST .../submit_for_approval, which refuses with 409 rather than route the offer differently from this plan. It covers the route, not the approvers: the submit resolves them afresh, so its request can ask people other than `rules` names. Absent from POST .../approval_plan, which answers for an edit not yet saved.
             */
            digest?: string;
        } | null;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/offer_letters/{external_id}/approval_plan',
            path: {
                'external_id': externalId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `the edit could not be saved: the offer is terminal, a placement is malformed, or a field the letter needs has no value`,
            },
        });
    }

    /**
     * Send an approved offer its stage held for a recruiter
     * Moves a `ready_to_send` offer to `approved` and queues its delivery to the applicant. Does not go back through approval: the decision is already recorded. Answers a repeat request for an offer already `approved` with the offer as it stands.
     * @param externalId
     * @returns any the offer, now sending
     * @throws ApiError
     */
    public static postInternalApiOfferLettersSendToApplicant(
        externalId: string,
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        status?: 'draft' | 'pending_approval' | 'approved' | 'ready_to_send' | 'send_failed' | 'sent' | 'completed' | 'canceled';
        /**
         * generated from a template, or an uploaded PDF override (AXHE-3720)
         */
        source?: 'generated' | 'uploaded';
        contract_type?: string | null;
        field_values?: Record<string, string>;
        /**
         * data key -> value the recruiter entered for a key driving a conditional section. Absent keys resolve from document-merge data (applicant, opening, or offer) (AXHE-4001)
         */
        condition_values?: Record<string, string>;
        /**
         * which controls this offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so a form editing this offer resolves them under this report
         */
        edited_keys?: {
            /**
             * template field keys
             */
            field_values?: Array<string>;
            /**
             * condition data keys
             */
            condition_values?: Array<string>;
        };
        /**
         * presigned URL to the rendered preview PDF
         */
        rendered_pdf_url?: string | null;
        /**
         * signature/date/initials field placements for an uploaded offer PDF (AXHE-4085). Send [] to clear. Omit the key to leave the stored placements unchanged.
         */
        signature_field_placements?: Array<{
            /**
             * field kind; `date` is auto-filled by Dropbox Sign at signing time
             */
            type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
            /**
             * 0-based index into the signers array (candidate first). Must be < the number of signers this offer will be sent with; an uploaded override has a candidate-only roster, so only 0 is valid there today.
             */
            signer: number;
            /**
             * 1-based page number; must not exceed the offer's rendered PDF page count, unless the PDF cannot be read (skips the upper bound)
             */
            page: number;
            /**
             * left edge, 72 DPI points from the left of the page
             */
            'x': number;
            /**
             * top edge, 72 DPI points down from the top of the page
             */
            'y': number;
            /**
             * width in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            width: number;
            /**
             * height in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            height: number;
            /**
             * whether the signer must complete this field
             */
            required?: boolean;
        }>;
        approval?: {
            required?: boolean;
            /**
             * the approval request now deciding this offer, or null when none is open — approval was not required, it was never submitted, or the last request has been decided. A resubmission raises a NEW request, so this is how a caller following one across an edit finds it (AXHE-4759).
             */
            request_id?: string | null;
            /**
             * the newest approval request on this offer whether or not it is still open, or null when none was ever raised. Ask this, not `required`, whether an approval exists: `required` is recomputed from the rules as they stand now, so a rule narrowed after the offer cleared it reads false on an offer carrying a granted sign-off (AXHE-4952).
             */
            latest_request_id?: string | null;
            /**
             * how many people the open request has emailed on the levels it is asking now, each counted once. An approver who could not be emailed is not counted, nor is a later level or a level a partial re-ask kept. 0 when no request is open.
             */
            notified_approver_count?: number;
            /**
             * approver groups, one per triggering rule
             */
            groups?: Array<{
                rule?: {
                    label?: string;
                    description?: string;
                    field_key?: string | null;
                };
                approvers?: Array<{
                    name?: string;
                    email?: string;
                }>;
            }>;
            /**
             * how the newest request was measured against the approval this offer was edited from, and which levels it therefore did not ask again. Backward-looking, unlike `required` and `groups`: it describes the request that exists, not the one a send from here would raise.
             */
            reask?: {
                /**
                 * `full` asked every applicable level, as a first submission does. `partial` asked some and kept the rest. `carried` asked none and kept them all, so the request was born approved. `exempt` asked none because the stage exempts edits from re-approval. NULL when nothing was measured — every submission on an account without partial re-approval, and every first send on one with it.
                 */
                scope?: 'full' | 'partial' | 'carried' | 'exempt' | null;
                /**
                 * the levels the newest request kept rather than asked, named rather than identified: a client that needs the ladder itself reads it from internal_api/approvals/requests/:external_id, which renders these in position order beside the asked ones.
                 */
                carried_levels?: Array<{
                    name?: string | null;
                    /**
                     * 1-based within its rule
                     */
                    position?: number | null;
                    rule_name?: string | null;
                }>;
            };
            /**
             * what a submit from here would do, read through the approval engine with the policy the submit uses, so an edit is measured the way the submit will measure it. Forward-looking, unlike `reask`. Null when a submit from here decides nothing: a terminal or held offer, one whose approval already released it, or one waiting on an open request.
             */
            plan?: {
                /**
                 * `approval`: the offer waits on the levels `rules` shows `pending`. `send`: it goes to the applicant. `hold`: nobody is asked, and the stage holds the approved offer for a recruiter to send.
                 */
                outcome?: 'approval' | 'send' | 'hold';
                /**
                 * the ladder the request a submit raises would show, in the shape of `rule_groups` on GET internal_api/approvals/requests/{external_id}. A level the submit asks reads `pending`, or `skipped` when its condition does not match; the submit opens each rule's first one. A level an edit keeps reads `approved` and `carried`. Approver emails and comments are null. Empty when no request would be raised.
                 */
                rules?: Array<any>;
                /**
                 * send back as `approval_plan_digest` with POST .../submit_for_approval, which refuses with 409 rather than route the offer differently from this plan. It covers the route, not the approvers: the submit resolves them afresh, so its request can ask people other than `rules` names. Absent from POST .../approval_plan, which answers for an edit not yet saved.
                 */
                digest?: string;
            } | null;
        };
        cta?: 'send_for_approval' | 'send_to_candidate';
        /**
         * whether this account may replace the generated doc with an uploaded PDF (AXHE-3720)
         */
        upload_override_enabled?: boolean;
        /**
         * whether POST .../revert would be accepted for this offer (AXHE-4266): it is an upload override, is not terminal, and still has a template to render. Drives the "Revert to template" control — do not infer it from `source` alone
         */
        can_revert_to_template?: boolean;
        /**
         * whether POST .../revise would be accepted for this offer (AXHE-4425): it is sent, is not an upload override, and no signer has signed it yet. Drives the "Edit" control — do not infer it from `status` alone, which cannot tell a signed offer from an unsigned one
         */
        can_revise?: boolean;
        /**
         * whether POST .../cancel would be accepted for this offer (AXHE-4682): it is sent, pending approval, approved, ready to send, or send-failed; is not an unsent revision; and no signer has signed it. Drives the "Cancel offer" control — do not infer it from `status` alone
         */
        can_cancel?: boolean;
        /**
         * whether POST .../discard_edit would be accepted for this offer: it is the draft an edit made of an offer its stage held at approval, nothing has replaced its document since, and the approval it was held under is still its newest request. Drives "Discard changes" on that edit — do not infer it from `status` alone
         */
        can_discard_edit?: boolean;
        /**
         * whether this offer replaces an earlier one. Its CTA reads "Re-send to applicant" rather than "Send"
         */
        revision?: boolean;
        /**
         * whether the stage's landing job sent this offer with no recruiter
         */
        auto_sent?: boolean;
        /**
         * past versions of this offer, newest first — the version history panel (AXHE-4425). Excludes the offer being viewed, which is the one currently in the applicant's portal. Empty for a first-time offer. Download a version through GET /internal_api/offer_letters/{its id}/download
         */
        versions?: Array<{
            /**
             * external_id of that version
             */
            id?: string;
            /**
             * when that version went to the candidate (ISO 8601)
             */
            sent_at?: string | null;
            /**
             * display name of the user who sent it; null if that user has since been removed, or if the stage's landing job sent it (see `auto_sent`)
             */
            sent_by?: string | null;
            /**
             * whether the stage's landing job sent that version with no recruiter
             */
            auto_sent?: boolean;
            /**
             * whether the applicant has stopped holding this version (AXHE-4950): true unless it is still `sent`. Do not infer it from the row's position — a supersede whose vendor void failed leaves the predecessor live, and it is still listed here
             */
            retired?: boolean;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/offer_letters/{external_id}/send_to_applicant',
            path: {
                'external_id': externalId,
            },
            errors: {
                422: `the offer is not waiting to be sent, its stage no longer resolves its template, or the applicant has signed an offer for the same template`,
            },
        });
    }

    /**
     * Throw away an edit of an offer its stage held at approval
     * Puts back the values, condition answers and signature fields the held offer had before the edit, re-renders the letter from them, and returns the offer to `ready_to_send` under the approval it already had. The edit was made on the held offer's own row (PATCH demotes it to `draft`), so it is undone in place; DELETE discards a revision, which is a row of its own. Refused once anything moved since the edit: a submit, a replaced document, or a newer approval request.
     * @param externalId
     * @returns any the held offer, ready to send again
     * @throws ApiError
     */
    public static postInternalApiOfferLettersDiscardEdit(
        externalId: string,
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        status?: 'draft' | 'pending_approval' | 'approved' | 'ready_to_send' | 'send_failed' | 'sent' | 'completed' | 'canceled';
        /**
         * generated from a template, or an uploaded PDF override (AXHE-3720)
         */
        source?: 'generated' | 'uploaded';
        contract_type?: string | null;
        field_values?: Record<string, string>;
        /**
         * data key -> value the recruiter entered for a key driving a conditional section. Absent keys resolve from document-merge data (applicant, opening, or offer) (AXHE-4001)
         */
        condition_values?: Record<string, string>;
        /**
         * which controls this offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so a form editing this offer resolves them under this report
         */
        edited_keys?: {
            /**
             * template field keys
             */
            field_values?: Array<string>;
            /**
             * condition data keys
             */
            condition_values?: Array<string>;
        };
        /**
         * presigned URL to the rendered preview PDF
         */
        rendered_pdf_url?: string | null;
        /**
         * signature/date/initials field placements for an uploaded offer PDF (AXHE-4085). Send [] to clear. Omit the key to leave the stored placements unchanged.
         */
        signature_field_placements?: Array<{
            /**
             * field kind; `date` is auto-filled by Dropbox Sign at signing time
             */
            type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
            /**
             * 0-based index into the signers array (candidate first). Must be < the number of signers this offer will be sent with; an uploaded override has a candidate-only roster, so only 0 is valid there today.
             */
            signer: number;
            /**
             * 1-based page number; must not exceed the offer's rendered PDF page count, unless the PDF cannot be read (skips the upper bound)
             */
            page: number;
            /**
             * left edge, 72 DPI points from the left of the page
             */
            'x': number;
            /**
             * top edge, 72 DPI points down from the top of the page
             */
            'y': number;
            /**
             * width in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            width: number;
            /**
             * height in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            height: number;
            /**
             * whether the signer must complete this field
             */
            required?: boolean;
        }>;
        approval?: {
            required?: boolean;
            /**
             * the approval request now deciding this offer, or null when none is open — approval was not required, it was never submitted, or the last request has been decided. A resubmission raises a NEW request, so this is how a caller following one across an edit finds it (AXHE-4759).
             */
            request_id?: string | null;
            /**
             * the newest approval request on this offer whether or not it is still open, or null when none was ever raised. Ask this, not `required`, whether an approval exists: `required` is recomputed from the rules as they stand now, so a rule narrowed after the offer cleared it reads false on an offer carrying a granted sign-off (AXHE-4952).
             */
            latest_request_id?: string | null;
            /**
             * how many people the open request has emailed on the levels it is asking now, each counted once. An approver who could not be emailed is not counted, nor is a later level or a level a partial re-ask kept. 0 when no request is open.
             */
            notified_approver_count?: number;
            /**
             * approver groups, one per triggering rule
             */
            groups?: Array<{
                rule?: {
                    label?: string;
                    description?: string;
                    field_key?: string | null;
                };
                approvers?: Array<{
                    name?: string;
                    email?: string;
                }>;
            }>;
            /**
             * how the newest request was measured against the approval this offer was edited from, and which levels it therefore did not ask again. Backward-looking, unlike `required` and `groups`: it describes the request that exists, not the one a send from here would raise.
             */
            reask?: {
                /**
                 * `full` asked every applicable level, as a first submission does. `partial` asked some and kept the rest. `carried` asked none and kept them all, so the request was born approved. `exempt` asked none because the stage exempts edits from re-approval. NULL when nothing was measured — every submission on an account without partial re-approval, and every first send on one with it.
                 */
                scope?: 'full' | 'partial' | 'carried' | 'exempt' | null;
                /**
                 * the levels the newest request kept rather than asked, named rather than identified: a client that needs the ladder itself reads it from internal_api/approvals/requests/:external_id, which renders these in position order beside the asked ones.
                 */
                carried_levels?: Array<{
                    name?: string | null;
                    /**
                     * 1-based within its rule
                     */
                    position?: number | null;
                    rule_name?: string | null;
                }>;
            };
            /**
             * what a submit from here would do, read through the approval engine with the policy the submit uses, so an edit is measured the way the submit will measure it. Forward-looking, unlike `reask`. Null when a submit from here decides nothing: a terminal or held offer, one whose approval already released it, or one waiting on an open request.
             */
            plan?: {
                /**
                 * `approval`: the offer waits on the levels `rules` shows `pending`. `send`: it goes to the applicant. `hold`: nobody is asked, and the stage holds the approved offer for a recruiter to send.
                 */
                outcome?: 'approval' | 'send' | 'hold';
                /**
                 * the ladder the request a submit raises would show, in the shape of `rule_groups` on GET internal_api/approvals/requests/{external_id}. A level the submit asks reads `pending`, or `skipped` when its condition does not match; the submit opens each rule's first one. A level an edit keeps reads `approved` and `carried`. Approver emails and comments are null. Empty when no request would be raised.
                 */
                rules?: Array<any>;
                /**
                 * send back as `approval_plan_digest` with POST .../submit_for_approval, which refuses with 409 rather than route the offer differently from this plan. It covers the route, not the approvers: the submit resolves them afresh, so its request can ask people other than `rules` names. Absent from POST .../approval_plan, which answers for an edit not yet saved.
                 */
                digest?: string;
            } | null;
        };
        cta?: 'send_for_approval' | 'send_to_candidate';
        /**
         * whether this account may replace the generated doc with an uploaded PDF (AXHE-3720)
         */
        upload_override_enabled?: boolean;
        /**
         * whether POST .../revert would be accepted for this offer (AXHE-4266): it is an upload override, is not terminal, and still has a template to render. Drives the "Revert to template" control — do not infer it from `source` alone
         */
        can_revert_to_template?: boolean;
        /**
         * whether POST .../revise would be accepted for this offer (AXHE-4425): it is sent, is not an upload override, and no signer has signed it yet. Drives the "Edit" control — do not infer it from `status` alone, which cannot tell a signed offer from an unsigned one
         */
        can_revise?: boolean;
        /**
         * whether POST .../cancel would be accepted for this offer (AXHE-4682): it is sent, pending approval, approved, ready to send, or send-failed; is not an unsent revision; and no signer has signed it. Drives the "Cancel offer" control — do not infer it from `status` alone
         */
        can_cancel?: boolean;
        /**
         * whether POST .../discard_edit would be accepted for this offer: it is the draft an edit made of an offer its stage held at approval, nothing has replaced its document since, and the approval it was held under is still its newest request. Drives "Discard changes" on that edit — do not infer it from `status` alone
         */
        can_discard_edit?: boolean;
        /**
         * whether this offer replaces an earlier one. Its CTA reads "Re-send to applicant" rather than "Send"
         */
        revision?: boolean;
        /**
         * whether the stage's landing job sent this offer with no recruiter
         */
        auto_sent?: boolean;
        /**
         * past versions of this offer, newest first — the version history panel (AXHE-4425). Excludes the offer being viewed, which is the one currently in the applicant's portal. Empty for a first-time offer. Download a version through GET /internal_api/offer_letters/{its id}/download
         */
        versions?: Array<{
            /**
             * external_id of that version
             */
            id?: string;
            /**
             * when that version went to the candidate (ISO 8601)
             */
            sent_at?: string | null;
            /**
             * display name of the user who sent it; null if that user has since been removed, or if the stage's landing job sent it (see `auto_sent`)
             */
            sent_by?: string | null;
            /**
             * whether the stage's landing job sent that version with no recruiter
             */
            auto_sent?: boolean;
            /**
             * whether the applicant has stopped holding this version (AXHE-4950): true unless it is still `sent`. Do not infer it from the row's position — a supersede whose vendor void failed leaves the predecessor live, and it is still listed here
             */
            retired?: boolean;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/offer_letters/{external_id}/discard_edit',
            path: {
                'external_id': externalId,
            },
            errors: {
                422: `nothing to discard: the offer holds no edit of a held offer, or something moved since the edit`,
            },
        });
    }

    /**
     * Download the rendered offer document (redirects to a presigned URL)
     * @param externalId
     * @param fileType which file to serve (AXHE-4562). `pdf` (default) is the rendered PDF. `docx` is the MERGED document — variables resolved and conditional blocks applied — for a recruiter who needs to hand-edit a negotiated figure in Word. Its body text matches the PDF; it is the whole Word archive, so template metadata such as docProps and comments travels with it. Not the source template. Any other value is a 400.
     * @returns void
     * @throws ApiError
     */
    public static getInternalApiOfferLettersDownload(
        externalId: string,
        fileType?: 'pdf' | 'docx',
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/offer_letters/{external_id}/download',
            path: {
                'external_id': externalId,
            },
            query: {
                'file_type': fileType,
            },
            errors: {
                302: `redirects to the presigned url for the requested file`,
                400: `unrecognized file_type`,
                404: `no such file for this offer — the PDF or the merged docx has not been written`,
            },
        });
    }

    /**
     * Read the rendered offer PDF, server-side (AXHE-4547)
     * @param externalId
     * @returns binary the PDF bytes — read and returned directly, never a redirect
     * @throws ApiError
     */
    public static getInternalApiOfferLettersDocument(
        externalId: string,
    ): CancelablePromise<Blob> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/offer_letters/{external_id}/document',
            path: {
                'external_id': externalId,
            },
            errors: {
                404: `no rendered PDF for this offer`,
            },
        });
    }

    /**
     * Revert to template: discard an Upload Override and re-render from the template (AXHE-4266)
     * @param externalId
     * @returns any the offer, back on its template document and re-rendered
     * @throws ApiError
     */
    public static postInternalApiOfferLettersRevert(
        externalId: string,
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        status?: 'draft' | 'pending_approval' | 'approved' | 'ready_to_send' | 'send_failed' | 'sent' | 'completed' | 'canceled';
        /**
         * generated from a template, or an uploaded PDF override (AXHE-3720)
         */
        source?: 'generated' | 'uploaded';
        contract_type?: string | null;
        field_values?: Record<string, string>;
        /**
         * data key -> value the recruiter entered for a key driving a conditional section. Absent keys resolve from document-merge data (applicant, opening, or offer) (AXHE-4001)
         */
        condition_values?: Record<string, string>;
        /**
         * which controls this offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so a form editing this offer resolves them under this report
         */
        edited_keys?: {
            /**
             * template field keys
             */
            field_values?: Array<string>;
            /**
             * condition data keys
             */
            condition_values?: Array<string>;
        };
        /**
         * presigned URL to the rendered preview PDF
         */
        rendered_pdf_url?: string | null;
        /**
         * signature/date/initials field placements for an uploaded offer PDF (AXHE-4085). Send [] to clear. Omit the key to leave the stored placements unchanged.
         */
        signature_field_placements?: Array<{
            /**
             * field kind; `date` is auto-filled by Dropbox Sign at signing time
             */
            type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
            /**
             * 0-based index into the signers array (candidate first). Must be < the number of signers this offer will be sent with; an uploaded override has a candidate-only roster, so only 0 is valid there today.
             */
            signer: number;
            /**
             * 1-based page number; must not exceed the offer's rendered PDF page count, unless the PDF cannot be read (skips the upper bound)
             */
            page: number;
            /**
             * left edge, 72 DPI points from the left of the page
             */
            'x': number;
            /**
             * top edge, 72 DPI points down from the top of the page
             */
            'y': number;
            /**
             * width in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            width: number;
            /**
             * height in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            height: number;
            /**
             * whether the signer must complete this field
             */
            required?: boolean;
        }>;
        approval?: {
            required?: boolean;
            /**
             * the approval request now deciding this offer, or null when none is open — approval was not required, it was never submitted, or the last request has been decided. A resubmission raises a NEW request, so this is how a caller following one across an edit finds it (AXHE-4759).
             */
            request_id?: string | null;
            /**
             * the newest approval request on this offer whether or not it is still open, or null when none was ever raised. Ask this, not `required`, whether an approval exists: `required` is recomputed from the rules as they stand now, so a rule narrowed after the offer cleared it reads false on an offer carrying a granted sign-off (AXHE-4952).
             */
            latest_request_id?: string | null;
            /**
             * how many people the open request has emailed on the levels it is asking now, each counted once. An approver who could not be emailed is not counted, nor is a later level or a level a partial re-ask kept. 0 when no request is open.
             */
            notified_approver_count?: number;
            /**
             * approver groups, one per triggering rule
             */
            groups?: Array<{
                rule?: {
                    label?: string;
                    description?: string;
                    field_key?: string | null;
                };
                approvers?: Array<{
                    name?: string;
                    email?: string;
                }>;
            }>;
            /**
             * how the newest request was measured against the approval this offer was edited from, and which levels it therefore did not ask again. Backward-looking, unlike `required` and `groups`: it describes the request that exists, not the one a send from here would raise.
             */
            reask?: {
                /**
                 * `full` asked every applicable level, as a first submission does. `partial` asked some and kept the rest. `carried` asked none and kept them all, so the request was born approved. `exempt` asked none because the stage exempts edits from re-approval. NULL when nothing was measured — every submission on an account without partial re-approval, and every first send on one with it.
                 */
                scope?: 'full' | 'partial' | 'carried' | 'exempt' | null;
                /**
                 * the levels the newest request kept rather than asked, named rather than identified: a client that needs the ladder itself reads it from internal_api/approvals/requests/:external_id, which renders these in position order beside the asked ones.
                 */
                carried_levels?: Array<{
                    name?: string | null;
                    /**
                     * 1-based within its rule
                     */
                    position?: number | null;
                    rule_name?: string | null;
                }>;
            };
            /**
             * what a submit from here would do, read through the approval engine with the policy the submit uses, so an edit is measured the way the submit will measure it. Forward-looking, unlike `reask`. Null when a submit from here decides nothing: a terminal or held offer, one whose approval already released it, or one waiting on an open request.
             */
            plan?: {
                /**
                 * `approval`: the offer waits on the levels `rules` shows `pending`. `send`: it goes to the applicant. `hold`: nobody is asked, and the stage holds the approved offer for a recruiter to send.
                 */
                outcome?: 'approval' | 'send' | 'hold';
                /**
                 * the ladder the request a submit raises would show, in the shape of `rule_groups` on GET internal_api/approvals/requests/{external_id}. A level the submit asks reads `pending`, or `skipped` when its condition does not match; the submit opens each rule's first one. A level an edit keeps reads `approved` and `carried`. Approver emails and comments are null. Empty when no request would be raised.
                 */
                rules?: Array<any>;
                /**
                 * send back as `approval_plan_digest` with POST .../submit_for_approval, which refuses with 409 rather than route the offer differently from this plan. It covers the route, not the approvers: the submit resolves them afresh, so its request can ask people other than `rules` names. Absent from POST .../approval_plan, which answers for an edit not yet saved.
                 */
                digest?: string;
            } | null;
        };
        cta?: 'send_for_approval' | 'send_to_candidate';
        /**
         * whether this account may replace the generated doc with an uploaded PDF (AXHE-3720)
         */
        upload_override_enabled?: boolean;
        /**
         * whether POST .../revert would be accepted for this offer (AXHE-4266): it is an upload override, is not terminal, and still has a template to render. Drives the "Revert to template" control — do not infer it from `source` alone
         */
        can_revert_to_template?: boolean;
        /**
         * whether POST .../revise would be accepted for this offer (AXHE-4425): it is sent, is not an upload override, and no signer has signed it yet. Drives the "Edit" control — do not infer it from `status` alone, which cannot tell a signed offer from an unsigned one
         */
        can_revise?: boolean;
        /**
         * whether POST .../cancel would be accepted for this offer (AXHE-4682): it is sent, pending approval, approved, ready to send, or send-failed; is not an unsent revision; and no signer has signed it. Drives the "Cancel offer" control — do not infer it from `status` alone
         */
        can_cancel?: boolean;
        /**
         * whether POST .../discard_edit would be accepted for this offer: it is the draft an edit made of an offer its stage held at approval, nothing has replaced its document since, and the approval it was held under is still its newest request. Drives "Discard changes" on that edit — do not infer it from `status` alone
         */
        can_discard_edit?: boolean;
        /**
         * whether this offer replaces an earlier one. Its CTA reads "Re-send to applicant" rather than "Send"
         */
        revision?: boolean;
        /**
         * whether the stage's landing job sent this offer with no recruiter
         */
        auto_sent?: boolean;
        /**
         * past versions of this offer, newest first — the version history panel (AXHE-4425). Excludes the offer being viewed, which is the one currently in the applicant's portal. Empty for a first-time offer. Download a version through GET /internal_api/offer_letters/{its id}/download
         */
        versions?: Array<{
            /**
             * external_id of that version
             */
            id?: string;
            /**
             * when that version went to the candidate (ISO 8601)
             */
            sent_at?: string | null;
            /**
             * display name of the user who sent it; null if that user has since been removed, or if the stage's landing job sent it (see `auto_sent`)
             */
            sent_by?: string | null;
            /**
             * whether the stage's landing job sent that version with no recruiter
             */
            auto_sent?: boolean;
            /**
             * whether the applicant has stopped holding this version (AXHE-4950): true unless it is still `sent`. Do not infer it from the row's position — a supersede whose vendor void failed leaves the predecessor live, and it is still listed here
             */
            retired?: boolean;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/offer_letters/{external_id}/revert',
            path: {
                'external_id': externalId,
            },
            errors: {
                422: `the offer is not an upload override, is already sent, or has no template to go back to`,
            },
        });
    }

    /**
     * Open a revision of a sent offer, for a negotiated change (AXHE-4425)
     * Creates a new draft that supersedes the delivered offer, pre-populated with its values and rendered from its frozen document rather than the live template. Nothing the candidate can see changes: the previous offer stays `sent` and stays signable until the revision is re-sent through POST .../submit_for_approval, which retires it. Calling this again while a revision is open returns that revision rather than creating a second, so a double-click cannot orphan the recruiter's work — the predecessor is row-locked for the check and the create together, so concurrent calls serialise rather than both inserting. Refused once ANY signer has signed, because re-sending voids the previous signature request and a signature already collected would be destroyed with it.
     * @param externalId
     * @returns any the revision, ready to edit
     * @throws ApiError
     */
    public static postInternalApiOfferLettersRevise(
        externalId: string,
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        status?: 'draft' | 'pending_approval' | 'approved' | 'ready_to_send' | 'send_failed' | 'sent' | 'completed' | 'canceled';
        /**
         * generated from a template, or an uploaded PDF override (AXHE-3720)
         */
        source?: 'generated' | 'uploaded';
        contract_type?: string | null;
        field_values?: Record<string, string>;
        /**
         * data key -> value the recruiter entered for a key driving a conditional section. Absent keys resolve from document-merge data (applicant, opening, or offer) (AXHE-4001)
         */
        condition_values?: Record<string, string>;
        /**
         * which controls this offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so a form editing this offer resolves them under this report
         */
        edited_keys?: {
            /**
             * template field keys
             */
            field_values?: Array<string>;
            /**
             * condition data keys
             */
            condition_values?: Array<string>;
        };
        /**
         * presigned URL to the rendered preview PDF
         */
        rendered_pdf_url?: string | null;
        /**
         * signature/date/initials field placements for an uploaded offer PDF (AXHE-4085). Send [] to clear. Omit the key to leave the stored placements unchanged.
         */
        signature_field_placements?: Array<{
            /**
             * field kind; `date` is auto-filled by Dropbox Sign at signing time
             */
            type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
            /**
             * 0-based index into the signers array (candidate first). Must be < the number of signers this offer will be sent with; an uploaded override has a candidate-only roster, so only 0 is valid there today.
             */
            signer: number;
            /**
             * 1-based page number; must not exceed the offer's rendered PDF page count, unless the PDF cannot be read (skips the upper bound)
             */
            page: number;
            /**
             * left edge, 72 DPI points from the left of the page
             */
            'x': number;
            /**
             * top edge, 72 DPI points down from the top of the page
             */
            'y': number;
            /**
             * width in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            width: number;
            /**
             * height in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            height: number;
            /**
             * whether the signer must complete this field
             */
            required?: boolean;
        }>;
        approval?: {
            required?: boolean;
            /**
             * the approval request now deciding this offer, or null when none is open — approval was not required, it was never submitted, or the last request has been decided. A resubmission raises a NEW request, so this is how a caller following one across an edit finds it (AXHE-4759).
             */
            request_id?: string | null;
            /**
             * the newest approval request on this offer whether or not it is still open, or null when none was ever raised. Ask this, not `required`, whether an approval exists: `required` is recomputed from the rules as they stand now, so a rule narrowed after the offer cleared it reads false on an offer carrying a granted sign-off (AXHE-4952).
             */
            latest_request_id?: string | null;
            /**
             * how many people the open request has emailed on the levels it is asking now, each counted once. An approver who could not be emailed is not counted, nor is a later level or a level a partial re-ask kept. 0 when no request is open.
             */
            notified_approver_count?: number;
            /**
             * approver groups, one per triggering rule
             */
            groups?: Array<{
                rule?: {
                    label?: string;
                    description?: string;
                    field_key?: string | null;
                };
                approvers?: Array<{
                    name?: string;
                    email?: string;
                }>;
            }>;
            /**
             * how the newest request was measured against the approval this offer was edited from, and which levels it therefore did not ask again. Backward-looking, unlike `required` and `groups`: it describes the request that exists, not the one a send from here would raise.
             */
            reask?: {
                /**
                 * `full` asked every applicable level, as a first submission does. `partial` asked some and kept the rest. `carried` asked none and kept them all, so the request was born approved. `exempt` asked none because the stage exempts edits from re-approval. NULL when nothing was measured — every submission on an account without partial re-approval, and every first send on one with it.
                 */
                scope?: 'full' | 'partial' | 'carried' | 'exempt' | null;
                /**
                 * the levels the newest request kept rather than asked, named rather than identified: a client that needs the ladder itself reads it from internal_api/approvals/requests/:external_id, which renders these in position order beside the asked ones.
                 */
                carried_levels?: Array<{
                    name?: string | null;
                    /**
                     * 1-based within its rule
                     */
                    position?: number | null;
                    rule_name?: string | null;
                }>;
            };
            /**
             * what a submit from here would do, read through the approval engine with the policy the submit uses, so an edit is measured the way the submit will measure it. Forward-looking, unlike `reask`. Null when a submit from here decides nothing: a terminal or held offer, one whose approval already released it, or one waiting on an open request.
             */
            plan?: {
                /**
                 * `approval`: the offer waits on the levels `rules` shows `pending`. `send`: it goes to the applicant. `hold`: nobody is asked, and the stage holds the approved offer for a recruiter to send.
                 */
                outcome?: 'approval' | 'send' | 'hold';
                /**
                 * the ladder the request a submit raises would show, in the shape of `rule_groups` on GET internal_api/approvals/requests/{external_id}. A level the submit asks reads `pending`, or `skipped` when its condition does not match; the submit opens each rule's first one. A level an edit keeps reads `approved` and `carried`. Approver emails and comments are null. Empty when no request would be raised.
                 */
                rules?: Array<any>;
                /**
                 * send back as `approval_plan_digest` with POST .../submit_for_approval, which refuses with 409 rather than route the offer differently from this plan. It covers the route, not the approvers: the submit resolves them afresh, so its request can ask people other than `rules` names. Absent from POST .../approval_plan, which answers for an edit not yet saved.
                 */
                digest?: string;
            } | null;
        };
        cta?: 'send_for_approval' | 'send_to_candidate';
        /**
         * whether this account may replace the generated doc with an uploaded PDF (AXHE-3720)
         */
        upload_override_enabled?: boolean;
        /**
         * whether POST .../revert would be accepted for this offer (AXHE-4266): it is an upload override, is not terminal, and still has a template to render. Drives the "Revert to template" control — do not infer it from `source` alone
         */
        can_revert_to_template?: boolean;
        /**
         * whether POST .../revise would be accepted for this offer (AXHE-4425): it is sent, is not an upload override, and no signer has signed it yet. Drives the "Edit" control — do not infer it from `status` alone, which cannot tell a signed offer from an unsigned one
         */
        can_revise?: boolean;
        /**
         * whether POST .../cancel would be accepted for this offer (AXHE-4682): it is sent, pending approval, approved, ready to send, or send-failed; is not an unsent revision; and no signer has signed it. Drives the "Cancel offer" control — do not infer it from `status` alone
         */
        can_cancel?: boolean;
        /**
         * whether POST .../discard_edit would be accepted for this offer: it is the draft an edit made of an offer its stage held at approval, nothing has replaced its document since, and the approval it was held under is still its newest request. Drives "Discard changes" on that edit — do not infer it from `status` alone
         */
        can_discard_edit?: boolean;
        /**
         * whether this offer replaces an earlier one. Its CTA reads "Re-send to applicant" rather than "Send"
         */
        revision?: boolean;
        /**
         * whether the stage's landing job sent this offer with no recruiter
         */
        auto_sent?: boolean;
        /**
         * past versions of this offer, newest first — the version history panel (AXHE-4425). Excludes the offer being viewed, which is the one currently in the applicant's portal. Empty for a first-time offer. Download a version through GET /internal_api/offer_letters/{its id}/download
         */
        versions?: Array<{
            /**
             * external_id of that version
             */
            id?: string;
            /**
             * when that version went to the candidate (ISO 8601)
             */
            sent_at?: string | null;
            /**
             * display name of the user who sent it; null if that user has since been removed, or if the stage's landing job sent it (see `auto_sent`)
             */
            sent_by?: string | null;
            /**
             * whether the stage's landing job sent that version with no recruiter
             */
            auto_sent?: boolean;
            /**
             * whether the applicant has stopped holding this version (AXHE-4950): true unless it is still `sent`. Do not infer it from the row's position — a supersede whose vendor void failed leaves the predecessor live, and it is still listed here
             */
            retired?: boolean;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/offer_letters/{external_id}/revise',
            path: {
                'external_id': externalId,
            },
            errors: {
                422: `the offer is not in a state that can be revised — a draft is still editable in place, an upload override has no values to re-render, and a signed offer cannot be replaced without destroying the signature`,
            },
        });
    }

    /**
     * Cancel an offer outright — stop it, rather than replace it (AXHE-4682)
     * Marks the offer `canceled`, recording who and when. A sent offer's Dropbox Sign request is voided FIRST, so the candidate's document stops being signable, and its portal signing tile is removed; a refused void leaves the offer `sent` (still live, still signable) and returns 422 for the recruiter to retry. A pending-approval offer's open approval request is withdrawn, kept as a record rather than deleted. Accepted from `sent`, `pending_approval`, `approved`, `ready_to_send`, and `send_failed`. Refused for a draft (nothing is committed to anyone), for an unsent revision (discard the edit instead, via DELETE), for a signed offer (voiding would destroy the signature), and for terminal statuses. A canceled offer no longer counts as delivered, so the signing stage's document package re-gates until a new offer is sent.
     * @param externalId
     * @returns any the canceled offer
     * @throws ApiError
     */
    public static postInternalApiOfferLettersCancel(
        externalId: string,
    ): CancelablePromise<{
        /**
         * external_id (UUID)
         */
        id?: string;
        status?: 'draft' | 'pending_approval' | 'approved' | 'ready_to_send' | 'send_failed' | 'sent' | 'completed' | 'canceled';
        /**
         * generated from a template, or an uploaded PDF override (AXHE-3720)
         */
        source?: 'generated' | 'uploaded';
        contract_type?: string | null;
        field_values?: Record<string, string>;
        /**
         * data key -> value the recruiter entered for a key driving a conditional section. Absent keys resolve from document-merge data (applicant, opening, or offer) (AXHE-4001)
         */
        condition_values?: Record<string, string>;
        /**
         * which controls this offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so a form editing this offer resolves them under this report
         */
        edited_keys?: {
            /**
             * template field keys
             */
            field_values?: Array<string>;
            /**
             * condition data keys
             */
            condition_values?: Array<string>;
        };
        /**
         * presigned URL to the rendered preview PDF
         */
        rendered_pdf_url?: string | null;
        /**
         * signature/date/initials field placements for an uploaded offer PDF (AXHE-4085). Send [] to clear. Omit the key to leave the stored placements unchanged.
         */
        signature_field_placements?: Array<{
            /**
             * field kind; `date` is auto-filled by Dropbox Sign at signing time
             */
            type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox';
            /**
             * 0-based index into the signers array (candidate first). Must be < the number of signers this offer will be sent with; an uploaded override has a candidate-only roster, so only 0 is valid there today.
             */
            signer: number;
            /**
             * 1-based page number; must not exceed the offer's rendered PDF page count, unless the PDF cannot be read (skips the upper bound)
             */
            page: number;
            /**
             * left edge, 72 DPI points from the left of the page
             */
            'x': number;
            /**
             * top edge, 72 DPI points down from the top of the page
             */
            'y': number;
            /**
             * width in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            width: number;
            /**
             * height in 72 DPI points (rescaled to 80 DPI at send time — do not pre-scale)
             */
            height: number;
            /**
             * whether the signer must complete this field
             */
            required?: boolean;
        }>;
        approval?: {
            required?: boolean;
            /**
             * the approval request now deciding this offer, or null when none is open — approval was not required, it was never submitted, or the last request has been decided. A resubmission raises a NEW request, so this is how a caller following one across an edit finds it (AXHE-4759).
             */
            request_id?: string | null;
            /**
             * the newest approval request on this offer whether or not it is still open, or null when none was ever raised. Ask this, not `required`, whether an approval exists: `required` is recomputed from the rules as they stand now, so a rule narrowed after the offer cleared it reads false on an offer carrying a granted sign-off (AXHE-4952).
             */
            latest_request_id?: string | null;
            /**
             * how many people the open request has emailed on the levels it is asking now, each counted once. An approver who could not be emailed is not counted, nor is a later level or a level a partial re-ask kept. 0 when no request is open.
             */
            notified_approver_count?: number;
            /**
             * approver groups, one per triggering rule
             */
            groups?: Array<{
                rule?: {
                    label?: string;
                    description?: string;
                    field_key?: string | null;
                };
                approvers?: Array<{
                    name?: string;
                    email?: string;
                }>;
            }>;
            /**
             * how the newest request was measured against the approval this offer was edited from, and which levels it therefore did not ask again. Backward-looking, unlike `required` and `groups`: it describes the request that exists, not the one a send from here would raise.
             */
            reask?: {
                /**
                 * `full` asked every applicable level, as a first submission does. `partial` asked some and kept the rest. `carried` asked none and kept them all, so the request was born approved. `exempt` asked none because the stage exempts edits from re-approval. NULL when nothing was measured — every submission on an account without partial re-approval, and every first send on one with it.
                 */
                scope?: 'full' | 'partial' | 'carried' | 'exempt' | null;
                /**
                 * the levels the newest request kept rather than asked, named rather than identified: a client that needs the ladder itself reads it from internal_api/approvals/requests/:external_id, which renders these in position order beside the asked ones.
                 */
                carried_levels?: Array<{
                    name?: string | null;
                    /**
                     * 1-based within its rule
                     */
                    position?: number | null;
                    rule_name?: string | null;
                }>;
            };
            /**
             * what a submit from here would do, read through the approval engine with the policy the submit uses, so an edit is measured the way the submit will measure it. Forward-looking, unlike `reask`. Null when a submit from here decides nothing: a terminal or held offer, one whose approval already released it, or one waiting on an open request.
             */
            plan?: {
                /**
                 * `approval`: the offer waits on the levels `rules` shows `pending`. `send`: it goes to the applicant. `hold`: nobody is asked, and the stage holds the approved offer for a recruiter to send.
                 */
                outcome?: 'approval' | 'send' | 'hold';
                /**
                 * the ladder the request a submit raises would show, in the shape of `rule_groups` on GET internal_api/approvals/requests/{external_id}. A level the submit asks reads `pending`, or `skipped` when its condition does not match; the submit opens each rule's first one. A level an edit keeps reads `approved` and `carried`. Approver emails and comments are null. Empty when no request would be raised.
                 */
                rules?: Array<any>;
                /**
                 * send back as `approval_plan_digest` with POST .../submit_for_approval, which refuses with 409 rather than route the offer differently from this plan. It covers the route, not the approvers: the submit resolves them afresh, so its request can ask people other than `rules` names. Absent from POST .../approval_plan, which answers for an edit not yet saved.
                 */
                digest?: string;
            } | null;
        };
        cta?: 'send_for_approval' | 'send_to_candidate';
        /**
         * whether this account may replace the generated doc with an uploaded PDF (AXHE-3720)
         */
        upload_override_enabled?: boolean;
        /**
         * whether POST .../revert would be accepted for this offer (AXHE-4266): it is an upload override, is not terminal, and still has a template to render. Drives the "Revert to template" control — do not infer it from `source` alone
         */
        can_revert_to_template?: boolean;
        /**
         * whether POST .../revise would be accepted for this offer (AXHE-4425): it is sent, is not an upload override, and no signer has signed it yet. Drives the "Edit" control — do not infer it from `status` alone, which cannot tell a signed offer from an unsigned one
         */
        can_revise?: boolean;
        /**
         * whether POST .../cancel would be accepted for this offer (AXHE-4682): it is sent, pending approval, approved, ready to send, or send-failed; is not an unsent revision; and no signer has signed it. Drives the "Cancel offer" control — do not infer it from `status` alone
         */
        can_cancel?: boolean;
        /**
         * whether POST .../discard_edit would be accepted for this offer: it is the draft an edit made of an offer its stage held at approval, nothing has replaced its document since, and the approval it was held under is still its newest request. Drives "Discard changes" on that edit — do not infer it from `status` alone
         */
        can_discard_edit?: boolean;
        /**
         * whether this offer replaces an earlier one. Its CTA reads "Re-send to applicant" rather than "Send"
         */
        revision?: boolean;
        /**
         * whether the stage's landing job sent this offer with no recruiter
         */
        auto_sent?: boolean;
        /**
         * past versions of this offer, newest first — the version history panel (AXHE-4425). Excludes the offer being viewed, which is the one currently in the applicant's portal. Empty for a first-time offer. Download a version through GET /internal_api/offer_letters/{its id}/download
         */
        versions?: Array<{
            /**
             * external_id of that version
             */
            id?: string;
            /**
             * when that version went to the candidate (ISO 8601)
             */
            sent_at?: string | null;
            /**
             * display name of the user who sent it; null if that user has since been removed, or if the stage's landing job sent it (see `auto_sent`)
             */
            sent_by?: string | null;
            /**
             * whether the stage's landing job sent that version with no recruiter
             */
            auto_sent?: boolean;
            /**
             * whether the applicant has stopped holding this version (AXHE-4950): true unless it is still `sent`. Do not infer it from the row's position — a supersede whose vendor void failed leaves the predecessor live, and it is still listed here
             */
            retired?: boolean;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/offer_letters/{external_id}/cancel',
            path: {
                'external_id': externalId,
            },
            errors: {
                422: `the offer is not in a state that can be canceled — a draft has been committed to nobody, an unsent revision is discarded rather than canceled, and a signed offer cannot be voided without destroying the signature`,
            },
        });
    }

}